import React, { useEffect, useState } from 'react';
import { Tooltip } from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import CustomButton from '../../../../components/buttons/CustomButton';
import Toast from '../../../../components/support/Toast';
import { DataProviderInstance } from '../../../../api/DataProvider';
import { useWhatsAppSender } from '../../../../contexts/WhatsAppSenderContext';
import { CreatePDFBlob } from '../../../../utils/Utils';
import { normalizePhone } from '../../../../utils/phoneUtils';
import { getWhatsAppErrorMessage, isWhatsAppPolicyError } from '../../../../utils/whatsappErrors';
import { buildReportFileName } from '../../../../services/clinicalReportService';

const NO_PHONE_MESSAGE = 'Paciente não possui telefone cadastrado.';

/** PDF (blob) em base64, como o envio de documentos do WhatsApp espera. */
function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result).split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/**
 * "Enviar por WhatsApp" do plano alimentar: gera o mesmo PDF do botão "PDF" a
 * partir do template já montado (`getElement`) e envia como documento pelo
 * número próprio da clínica, como o envio de documentos do atendimento. Só
 * aparece quando a clínica pode enviar documentos (Z-API própria configurada
 * e capability `document`).
 *
 * Também serve a outros PDFs do atendimento (receituário): `reportType`,
 * `caption`, `label` e as mensagens trocam os textos; sem eles, valem os do plano.
 */
const MealPlanWhatsAppButton = ({
  patientId,
  patientName,
  getElement,
  reportType = 'plano-alimentar',
  caption = 'Segue o seu plano alimentar.',
  successMessage = 'Plano alimentar enviado por WhatsApp.',
  errorMessage = 'Não foi possível enviar o plano por WhatsApp. Tente novamente.',
  pdfErrorMessage = 'Não foi possível gerar o PDF do plano.',
  label = 'Enviar por WhatsApp',
}) => {
  const { GetPatientById, GetWhatsappConfig, SendWhatsappDocument } = DataProviderInstance();
  const { capabilities } = useWhatsAppSender();
  const [configured, setConfigured] = useState(false);
  const [sending, setSending] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    GetWhatsappConfig((result) => {
      const config = result && result.config;
      setConfigured(!!(config && config.instance_id && config.has_instance_token));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!configured || !capabilities.document) return null;

  const patient = GetPatientById(patientId) || {};
  const rawPhone = patient.phone || patient.cellphone || '';
  const hasPhone = !!rawPhone;

  async function send() {
    const element = getElement();
    if (!element || sending) return;
    const phone = normalizePhone(rawPhone, patient.countryCode);
    if (!phone) {
      setToast({ message: NO_PHONE_MESSAGE, severity: 'warning' });
      return;
    }
    setSending(true);
    try {
      const blob = await CreatePDFBlob(element);
      const base64 = await blobToBase64(blob);
      const fileName = buildReportFileName(patientName, reportType);
      SendWhatsappDocument(phone, base64, fileName, caption, patient.countryCode || '', (result) => {
        setSending(false);
        if (result && result.success) {
          setToast({ message: successMessage, severity: 'success' });
        } else {
          const message = isWhatsAppPolicyError(result)
            ? getWhatsAppErrorMessage(result)
            : errorMessage;
          setToast({ message, severity: 'error' });
        }
      });
    } catch (error) {
      setSending(false);
      setToast({ message: pdfErrorMessage, severity: 'error' });
    }
  }

  return (
    <>
      <Tooltip title={hasPhone ? '' : NO_PHONE_MESSAGE}>
        <span>
          <CustomButton
            variant="outlined"
            style="secondary"
            label={sending ? 'Enviando...' : label}
            icon={<WhatsAppIcon />}
            enabled={!sending && hasPhone}
            onClick={send}
          />
        </span>
      </Tooltip>
      {toast && (
        <Toast
          open
          onClose={() => setToast(null)}
          message={toast.message}
          severity={toast.severity}
          autoHideDuration={5000}
        />
      )}
    </>
  );
};

export default MealPlanWhatsAppButton;
